/**
 * Multi-route vehicle calculation utilities
 * 
 * These functions calculate vehicle requirements when multiple routes
 * are operating simultaneously, with support for dedicated and shared vehicle modes.
 */

import type { FixedRoute } from '../../types/fixedRoutes';
import type { RouteTimeWindow } from './fleetTypes';
import { calculateMinimumVehicles } from './vehicleCalculator';
import { calculateRouteLoopDuration } from './vehicleCalculator';

/**
 * Convert HH:mm time string to total minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Build route timeline with time windows for each route's operating period
 * 
 * @param routes - Array of fixed routes
 * @param trafficMultiplier - Traffic multiplier to apply to segment travel times
 * @returns Array of route time windows
 */
export function buildRouteTimeline(
  routes: FixedRoute[],
  trafficMultiplier: number
): RouteTimeWindow[] {
  return routes.map((route) => {
    const startMinutes = timeToMinutes(route.startTime);
    const endMinutes = route.endTime 
      ? timeToMinutes(route.endTime)
      : 22 * 60; // Default to 22:00 if not specified
    
    // Handle overnight shifts (end < start)
    let endTimeMinutes = endMinutes;
    if (endTimeMinutes < startMinutes) {
      endTimeMinutes = endTimeMinutes + (24 * 60); // Add 24 hours
    }
    
    const loopDuration = calculateRouteLoopDuration(route, trafficMultiplier);
    
    return {
      route,
      startTime: route.startTime,
      endTime: route.endTime || '22:00',
      loopDuration,
    };
  });
}

/**
 * Calculate minimum vehicles needed for routes in dedicated mode
 * Each route has its own dedicated vehicles (no sharing)
 * 
 * @param routes - Array of fixed routes
 * @param trafficMultiplier - Traffic multiplier to apply to segment travel times
 * @returns Total number of vehicles needed (sum of vehicles per route)
 */
export function calculateDedicatedMode(
  routes: FixedRoute[],
  trafficMultiplier: number
): number {
  let totalVehicles = 0;
  
  // Calculate vehicles for each route independently
  routes.forEach((route) => {
    const vehiclesForRoute = calculateMinimumVehicles(route, trafficMultiplier);
    totalVehicles += vehiclesForRoute;
  });
  
  return totalVehicles;
}

/**
 * Check if two time windows overlap
 */
function timeWindowsOverlap(
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean {
  // Handle overnight shifts
  const normalizedEnd1 = end1 < start1 ? end1 + (24 * 60) : end1;
  const normalizedEnd2 = end2 < start2 ? end2 + (24 * 60) : end2;
  const normalizedStart2 = end2 < start2 ? start2 + (24 * 60) : start2;
  
  return !(normalizedEnd1 <= start2 || normalizedStart2 <= start1);
}

/**
 * Calculate minimum vehicles needed for routes in shared mode
 * Vehicles can be shared between routes when schedules allow
 * 
 * Strategy:
 * 1. Build timeline of all route operations
 * 2. Identify overlapping time periods
 * 3. For each time point, calculate vehicles needed
 * 4. Return maximum vehicles needed at any point
 * 
 * @param routes - Array of fixed routes
 * @param trafficMultiplier - Traffic multiplier to apply to segment travel times
 * @returns Minimum number of vehicles needed (optimized for sharing)
 */
export function calculateSharedMode(
  routes: FixedRoute[],
  trafficMultiplier: number
): number {
  if (routes.length === 0) {
    return 0;
  }
  
  if (routes.length === 1) {
    // Single route: just calculate minimum vehicles for that route
    return calculateMinimumVehicles(routes[0], trafficMultiplier);
  }
  
  // Build timeline of route operations
  const timeline = buildRouteTimeline(routes, trafficMultiplier);
  
  // Create list of all time points (start and end times)
  const timePoints = new Set<number>();
  timeline.forEach((window) => {
    timePoints.add(timeToMinutes(window.startTime));
    const endMinutes = timeToMinutes(window.endTime);
    timePoints.add(endMinutes < timeToMinutes(window.startTime) ? endMinutes + (24 * 60) : endMinutes);
  });
  
  const sortedTimePoints = Array.from(timePoints).sort((a, b) => a - b);
  
  // For each time period between consecutive time points, calculate vehicles needed
  let maxVehiclesNeeded = 0;
  
  for (let i = 0; i < sortedTimePoints.length - 1; i++) {
    const periodStart = sortedTimePoints[i];
    const periodEnd = sortedTimePoints[i + 1];
    
    // Find routes that are active during this period
    const activeRoutes: RouteTimeWindow[] = [];
    
    timeline.forEach((window) => {
      const windowStart = timeToMinutes(window.startTime);
      let windowEnd = timeToMinutes(window.endTime);
      
      // Handle overnight shifts
      if (windowEnd < windowStart) {
        windowEnd = windowEnd + (24 * 60);
      }
      
      // Check if route is active during this period
      if (timeWindowsOverlap(periodStart, periodEnd, windowStart, windowEnd)) {
        activeRoutes.push(window);
      }
    });
    
    // Calculate vehicles needed for active routes
    // For circulators, we need vehicles based on loop duration and operating period
    // For simplicity in Phase 1A, sum the minimum vehicles for each active route
    // This can be optimized further in future phases
    let vehiclesForPeriod = 0;
    
    activeRoutes.forEach((window) => {
      const route = window.route;
      
      if (route.isCirculator) {
        // For circulators: need vehicles based on loop duration
        const loopDuration = window.loopDuration;
        const periodDuration = periodEnd - periodStart;
        
        // Calculate how many vehicles needed to cover this period for this route
        if (loopDuration > 0) {
          const vehiclesForRoute = Math.ceil(periodDuration / loopDuration) || 1;
          vehiclesForPeriod = Math.max(vehiclesForPeriod, vehiclesForRoute);
        } else {
          vehiclesForPeriod += 1;
        }
      } else {
        // For non-circulators: one vehicle per route that's active
        vehiclesForPeriod += 1;
      }
    });
    
    maxVehiclesNeeded = Math.max(maxVehiclesNeeded, vehiclesForPeriod);
  }
  
  // Fallback: if no overlaps, just sum minimum vehicles for all routes
  // This handles edge cases where time-based calculation might fail
  const sumOfMinVehicles = routes.reduce((sum, route) => {
    return sum + calculateMinimumVehicles(route, trafficMultiplier);
  }, 0);
  
  // Return the minimum of time-based calculation and simple sum
  // This ensures we don't over-calculate due to algorithm limitations
  return Math.min(maxVehiclesNeeded || sumOfMinVehicles, sumOfMinVehicles);
}

